/**
 * `createPlayer` — the player's one public surface (ARCHITECTURE.md §8).
 *
 * ```ts
 * const player = await createPlayer(hostEl, bundle, { baseUrl, mode: 'tap' })
 * player.on('state', (s) => hud.setState(s))
 * player.play()
 * ```
 *
 * The player is assembled from four owners and two ports:
 *
 * - `device.ts` decides the class and the texture budget;
 * - `loader.ts` resolves and loads bytes inside that budget;
 * - `runner.ts` owns story time, the cue schedule, the holds and the taps —
 *   all of it deterministic and headless;
 * - `stage.ts` + `world.ts` + `plates.ts` draw a `StageState` with pixi, and
 *   are imported **lazily**, so `createPlayer(null, bundle)` runs the whole
 *   story headless in vitest with no renderer at all.
 *
 * A story still being made (docs/STORY-STREAMING.md) opens on its first ready
 * part and grows in place: `appendBundle` folds each newer revision in
 * (`stream.ts`), and the runner waits at the end of the ready parts for more.
 *
 * The other two tracks plug in through `AudioPort` and `HudPort` below.
 * Both default to no-ops, so the player never blocks on audio (a cue with no
 * baked audio still plays with estimated word timing — BUILD-OVERRIDES) and
 * never blocks on a HUD.
 */

import type { Bundle } from '@/schema/story'

import { StoryClock, clampRate, rafFrameSource } from './clock'
import type { FrameSource } from './clock'
import { currentDevice, headlessDevice } from './device'
import type { DeviceOverrides, DeviceProfile } from './device'
import { normalizeBundle } from './bundle'
import { BundleLoader, baseUrlOfBundle, nullTexturePort, pixiTexturePort } from './loader'
import type { LoadProgress, TexturePort } from './loader'
import { TimelineRunner, cuesOf, cueDur, isLineCue, lineSpeaker, lineText, locateStoryTime, storyTimeline } from './runner'
import type { HoldKind, LineCue, Phase, RunnerPrefs, StageState, StoryTimeline, TapSource } from './runner'
import { STREAM_STALL_MS, expectsMore, isOpen, mergeGrownBundle, sameStream, streamOf } from './stream'
import type { MergeResult, StreamInfo } from './stream'
import { endingFor } from './transition'
import type { SceneEntry } from './transition'
import type { LineWord } from './webspeech'

export type { DeviceClass, DeviceProfile } from './device'
export type { BudgetPlan, LoadProgress, TexturePort } from './loader'
export type {
  ActorState,
  CameraState,
  HoldKind,
  LineCue,
  Phase,
  PropState,
  RunnerPrefs,
  StageState,
  StoryTimeline,
  TapResult,
  TapSource,
} from './runner'
export { MemoryBudget, baseUrlOfBundle, resolveAssetUrl } from './loader'
export { normalizeBundle } from './bundle'
export { TimelineRunner, applyCue, locateStoryTime, stageStateAt, storyTimeline } from './runner'
export { StoryClock } from './clock'
export type { SceneEntry } from './transition'
export { STREAM_STALL_MS, expectsMore, isOpen, mergeGrownBundle, streamOf, watchBundle } from './stream'
export type { BundleWatcher, MergeResult, StreamInfo } from './stream'

/* ------------------------------------------------------------ the stage */

/**
 * The logical stage of §8.1 — **one** definition for the whole player.
 * `stage.ts` (pixi) and `bubbles.ts` (pure layout, must stay pixi-free) both
 * read it from here rather than each carrying its own copy.
 */
export const STAGE_W = 1920
export const STAGE_H = 1080

/** How long the loading bar may wait on scene 0's baked voice before starting anyway. */
const VOICE_PRELOAD_MS = 4000
/**
 * A play-through (or "Again") keeps the frozen last frame on screen while the next scene loads;
 * the full-stage loading card comes up only when the load is still going after this long.
 */
export const LOAD_CARD_DELAY_MS = 300

/* ---------------------------------------------------------------- modes */

/** Playback modes of §8.6. `tap` never expires at a scene end; the other two autoplay. */
export type PlayerMode = 'tap' | 'read_to_me' | 'bedtime'

/* ---------------------------------------------------------------- ports */

/**
 * The audio track's side (`audio.ts`, `subtitles.ts`, `bubbles.ts`,
 * `webspeech.ts`). Every method must be safe to call at any time; the runner
 * never waits on one except `unlock()`.
 */
export interface AudioPort {
  /** One tap unlocks Web Audio, the video element and the wake lock (§8.4). */
  unlock(): Promise<void>
  /**
   * Play a `narrate` / `say` / `question` line. `whenMs` is the offset into
   * the line to start at (non-zero after a seek). Falls back to Web Speech —
   * or to silence with estimated word timing — when `cue.audio` is null.
   */
  playLine(cue: LineCue, whenMs: number): Promise<void>
  stopLine(): void
  /**
   * Decode the baked voice of these lines before they are needed.
   *
   * `playLine` never blocks, so an undecoded line plays through Web Speech and
   * only warms the buffer for the replay. Nothing else preloads voice bytes —
   * `AudioRef.url` is not a bundle asset id, so the texture loader skips it —
   * which is why the player calls this for scene 0 before the tap gate lifts
   * and for scene N+1 in the background. Optional: a port with no real audio
   * has nothing to decode.
   */
  preloadLines?(cues: readonly LineCue[]): Promise<void>
  preloadSounds?(uids: readonly string[]): Promise<void>
  stopSounds?(): void
  pauseBackground?(paused: boolean): void
  /** bgm 0.6 → 0.18 under speech, recover after 0.3 s (§8.4). */
  duck(on: boolean): void
  /** Bundle asset id of the music bed, or `null` to stop it. */
  music(uid: string | null, gainDb?: number, fade?: number): void
  sfx(uid: string, gainDb?: number, duration?: number): void
  /** Word-level karaoke callback; index is into `AudioRef.words[]`. */
  onWord(cb: (wordIndex: number) => void): void
  /** Milliseconds into the line currently playing — the clock's audio master (§8.1). */
  /**
   * Milliseconds into the line's decoded buffer, or **a negative number**
   * when nothing is mastering the clock (Web Speech, silence, not started).
   * `StoryClock` only follows a value `>= 0`.
   */
  currentLineTimeMs(): number
  /**
   * Playback speed (0.5–2), applied to the line playing (re-timed where it
   * stands, pitch kept) and to every line after it; `utterance.rate` on the
   * Web Speech fallback. `currentLineTimeMs` stays in *line* milliseconds, so
   * the clock (which runs at the same rate) keeps following the words.
   *
   * Optional so an older port (a test fake) still satisfies `AudioPort`; every
   * port in this repo implements it.
   */
  setRate?(rate: number): void
  /** Older name of `setRate`; used only when a port has no `setRate`. */
  setVoiceSpeed?(rate: number): void
}

/** Buttons and stage taps the HUD can send back. §8.6 decides which are visible per phase. */
export type PlayerControl =
  | 'play'
  | 'pause'
  | 'next'
  | 'again'
  | 'replay_scene'
  | 'previous_scene'
  | 'skip_scene'
  | 'restart'
  | 'exit'
  /** A story still being made: stop waiting for more parts ("End here", or the story is gone). */
  | 'end_stream'

/**
 * A grown-up seek (the seek bar in `controls.ts`, or a hotkey), in **story**
 * seconds — every scene end to end, see `storyTimeline`.
 */
export interface SeekRequest {
  t: number
  /**
   * A drag's lifecycle. `start` freezes the story and silences the voice,
   * `move` previews silently, `end` lets go and the line under the new time
   * speaks from its offset. Absent means a one-shot jump (click, key).
   */
  scrub?: 'start' | 'move' | 'end'
}

/** The HUD track's side (`hud.ts`, `standalone.ts`, `sw.ts`, `StoryPlayer.tsx`). */
export interface HudPort {
  setState(s: PlayerUiState): void
  onTap(cb: (source: TapSource) => void): void
  onControl(cb: (control: PlayerControl) => void): void
  /** Grown-up seek bar / hotkeys. Optional: a HUD without a seek bar never calls it. */
  onSeek?(cb: (req: SeekRequest) => void): void
  /**
   * Stage-percent positions for the DOM `?` and the tap-gate hand, supplied by
   * the renderer's bubble layer (§8.6: the glyph is DOM so it keeps the 88 px
   * floor; only its *position* comes from the stage). Optional: a HUD that
   * ignores it keeps its default corner.
   */
  setAnchors?(a: { question?: readonly [number, number] | null; gate?: readonly [number, number] | null }): void
}

/** Everything the HUD and the subtitle band render from. The single published UI contract. */
export interface PlayerUiState {
  mode: PlayerMode
  /** §8.1's visible states. A `scene_end` or `tap_gate` hold is `holding`; a question is its own phase. */
  phase: Phase
  /** Which hold we are in, when `phase` is `holding` or `question`. */
  holdKind: HoldKind | null
  /** The checkpoint's label ("The end — again?", "Tap to make Pip splash!"). */
  holdLabel: string | null
  /** True before the unlock gesture: the HUD shows "Tap to start" (§8.4). */
  needsGesture: boolean
  /** Byte-weighted scene load (§8.5). `active` is the spinner/progress bar. */
  loading: { active: boolean; progress: number }
  /** ↺ *again* is enabled only when there is an earlier line (§8.6). */
  canReplayLine: boolean
  sceneIndex: number
  sceneCount: number
  /** Scene-local story seconds, and the scene's baked duration. */
  t: number
  sceneDuration: number
  /*
   * The whole-story timeline, for the grown-up seek bar and "0:12 / 0:34".
   * `createPlayer` always sets all four; they are optional only so a HUD test
   * that builds a state by hand (or an older HUD fake) still type-checks — a
   * reader falls back to `t` / `sceneDuration` / `[0]` / `false`.
   */
  /** Story seconds elapsed across every scene (holds excluded). `storyDuration` once ended. */
  storyT?: number
  /** Sum of every scene's baked duration. */
  storyDuration?: number
  /** Story second at which each scene starts — the seek bar marks the boundaries. */
  sceneStarts?: readonly number[]
  /** The seek bar is being dragged: the story is frozen on the preview frame. */
  scrubbing?: boolean
  /** The live line. `wordIndex` is −1 before the first word. */
  subtitle: { text: string; wordIndex: number; speaker: string | null } | null
  /** Dimmed to 20 % after `idle_dim_s` at a hold; any tap restores it (§8.6). */
  dimmed: boolean
  /** Grown-up panel state the HUD mirrors. */
  questions: boolean
  subtitles: boolean
  reducedMotion: boolean
  lowDistraction?: boolean
  deviceClass: DeviceProfile['class']
  /** Playback speed, 0.5–2. Optional only so a hand-built state (a HUD test) still type-checks; reads fall back to 1. */
  rate?: number
  /**
   * How much of the story exists yet (`stream.ts`): `complete` for every finished story. The HUD
   * draws the wait for the next part, "it will go on later" and "this story stopped early" from
   * it. `createPlayer` always sets it; optional for the same hand-built states as above.
   */
  stream?: StreamInfo
  /**
   * The child has waited `STREAM_STALL_MS` at the end of the ready parts with no new revision (a
   * story stopped without saying so, e.g. its job was cancelled): the wait offers "End here" too.
   */
  streamStalled?: boolean
  /**
   * A `failed` story really stopped short of its parts. False for one that failed after its last
   * part was published (it played every part and its ending). Absent in a hand-built state: a
   * failed stream is taken as short.
   */
  stoppedShort?: boolean
}

/* -------------------------------------------------------- renderer seam */

/**
 * What `stage.ts` + `world.ts` provide. Declared here so the player can be
 * built without them (headless), and so a future renderer (the ADDENDUM's
 * puppet path, a canvas2d fallback) drops in without touching the runner.
 */
export interface RendererPort {
  /** Draw one frame of the pure snapshot. Called once per rAF tick. */
  render(state: StageState, ctx: RenderContext): void
  /**
   * A new scene became current: rebuild the plate and the actor views. `capture` (default: on
   * whenever a scene is showing) first snapshots the frame on screen — the old picture a
   * play-through carries away, and what the child sees while the next scene is built.
   */
  setScene(index: number, opts?: { capture?: boolean }): void | Promise<void>
  resize(): void
  destroy(): void
  /** §8.6 *Motion*: poster instead of video, damped camera, `run/jump → walk/happy`. */
  setReducedMotion?(on: boolean): void
  setLowDistraction?(on: boolean): void
  /** Stage-percent anchors the HUD's DOM affordances follow, or nulls. */
  anchors?(): { question: readonly [number, number] | null; gate: readonly [number, number] | null }
}

export interface RenderContext {
  bundle: Bundle
  sceneIndex: number
  device: DeviceProfile
  loader: BundleLoader
  reducedMotion: boolean
  lowDistraction?: boolean
  /** Presentation-only wall clock: the tap-gate anticipation loop (ADDENDUM §5.3). */
  wallMs: number
  /** The hold we are in, so the renderer can loop a gated verb's anticipation step. */
  holdKind: HoldKind | null
  /** Actor of the cue a `tap_gate` hold is gating — where the hand glyph belongs. */
  gatedActor?: string | null
  /*
   * Scene transitions (SKEL-STORY "Scene transitions"). All optional: a context without them
   * draws no transition at all (an older caller, a test).
   */
  /** The runner's raw scene clock: below 0 during a transition's pre-roll, past `duration` in the ending. */
  sceneT?: number
  /** How the current scene was entered; absent means `'jump'` (a cut). */
  entry?: SceneEntry
  /** A scene is loading after a "Next": draw nothing new, keep the frame on screen. */
  between?: boolean
  /** The story's ending is playing (or the story ended on one). */
  outro?: boolean
}

/* --------------------------------------------------------------- events */

export interface PlayerEvents {
  state: (s: PlayerUiState) => void
  progress: (p: LoadProgress) => void
  line: (cue: LineCue) => void
  hold: (kind: HoldKind, label: string) => void
  /** ADDENDUM §5.3: spoken nudge at 6 s and 12 s of a tap gate. */
  nudge: (index: number, kind: HoldKind) => void
  sceneChange: (index: number) => void
  ended: () => void
  error: (err: unknown) => void
  /** A story still being made grew, paused, failed or completed (`appendBundle`). */
  stream: (s: StreamInfo) => void
}

/**
 * Everything the grown-up panel can change, in the player's own words.
 * `RunnerPrefs` covers mode/questions/auto-answer/holds; the three below are
 * owned by the player itself because they cross ports: subtitles are DOM,
 * voice speed is the audio port, reduced motion is the renderer.
 */
export interface PlayerPrefs extends Partial<RunnerPrefs> {
  subtitles?: boolean
  /** Playback speed, 0.5–2: the story clock, every voice and the karaoke together. */
  rate?: number
  /** Older name of `rate` (the panel's "voice speed"). `rate` wins when both are given. */
  voiceSpeed?: number
  /** §8.6 *Motion*. Defaults to the device's `prefers-reduced-motion`. */
  reducedMotion?: boolean
  /** Hide decorative effects/camera moves, never the action or teaching focus. */
  lowDistraction?: boolean
}

export interface CreatePlayerOptions {
  /** Defaults to the directory of the bundle URL (§4.6.4). Pass the bundle URL as `bundleUrl` instead. */
  baseUrl?: string
  /** The url `bundle.json` was fetched from; its directory becomes `baseUrl`. */
  bundleUrl?: string
  mode?: PlayerMode
  device?: DeviceProfile | DeviceOverrides
  audio?: AudioPort
  hud?: HudPort
  /** Grown-up-panel starting values; defaults come from the bundle's band (§4.6.1). */
  prefs?: PlayerPrefs
  /** Swap the renderer (tests, a future puppet renderer). `false` = headless. */
  renderer?: RendererPort | false
  textures?: TexturePort
  frames?: FrameSource
  /** Wall clock, injected for tests. */
  now?: () => number
  /** Start playing as soon as scene 0 is loaded, without waiting for a gesture. */
  autoplay?: boolean
}

export interface PlayerHandle {
  readonly bundle: Bundle
  readonly device: DeviceProfile
  readonly runner: TimelineRunner
  readonly loader: BundleLoader
  readonly state: PlayerUiState
  /** Every scene end to end — what `seekStory` and `PlayerUiState.storyT` measure. Grows with the story. */
  readonly timeline: StoryTimeline
  /** How much of the story exists yet; `complete` for a finished one. */
  readonly stream: StreamInfo
  /** This session waits for no more parts (`endStream`, "End here"): nothing is left to poll for. */
  readonly streamEnded: boolean

  /**
   * Wait for no more parts: the parts here play, then the story ends ("End here" is this, as the
   * `end_stream` control). `failed`: the story itself stopped short (its job was cancelled or lost,
   * and the page knows before `bundle.json` says so), so the end screen says it stopped early; a
   * part published before the stop still lands (the mount's poll takes a last look). Emits `stream`.
   */
  endStream(opts?: { failed?: boolean }): void

  /**
   * A newer revision of a story still being made (`stream.ts#watchBundle` fetched it). Folded into
   * `bundle` in place: new assets are registered, new parts are appended (a child waiting at the
   * end plays on at once), and the cast and prop tables change at the next scene load, never under
   * the scene on screen. Returns what it added.
   */
  appendBundle(next: Bundle): MergeResult

  /** Unlock audio + wake lock and start. Safe to call from a click handler. */
  start(): Promise<void>
  play(): void
  pause(): void
  toggle(): void
  /** Seek within the current scene (scene-local seconds). */
  seek(t: number): void
  /**
   * Seek anywhere in the story (story seconds, clamped), crossing scenes when
   * needed. Keeps the play state: playing keeps playing with the voice
   * restarted at the right offset, paused stays paused, and a seek out of a
   * hold or from the end plays on. Scene jumps are serialised, so a drag that
   * fires many seeks while a scene loads lands on the *last* one.
   */
  seekStory(t: number): Promise<void>
  /** Seek-bar drag in progress: freeze the story and keep the voice silent until it ends. */
  setScrubbing(on: boolean): void
  replayLastLine(): boolean
  skipScene(): Promise<void>
  previousScene(): Promise<void>
  replayScene(): Promise<void>
  restart(): Promise<void>
  /** The one tap entry point (§8.1); the HUD forwards every stage tap here. */
  tap(source?: TapSource): void
  control(c: PlayerControl): void
  setMode(m: PlayerMode): void
  setPrefs(p: PlayerPrefs): void
  on<K extends keyof PlayerEvents>(ev: K, cb: PlayerEvents[K]): () => void
  destroy(): void
}

/* ---------------------------------------------------------- no-op ports */

/** The default audio: silent, but with a clock so lines still advance (BUILD-OVERRIDES). */
export function nullAudioPort(): AudioPort {
  return {
    unlock: async () => {},
    playLine: async () => {},
    stopLine: () => {},
    preloadLines: async () => {},
    duck: () => {},
    music: () => {},
    sfx: () => {},
    onWord: () => {},
    // `−1` means "no audio master": StoryClock falls back to its own stepping.
    currentLineTimeMs: () => -1,
    setRate: () => {},
    setVoiceSpeed: () => {},
  }
}

export function nullHudPort(): HudPort {
  return { setState: () => {}, onTap: () => {}, onControl: () => {} }
}

/* ------------------------------------------------------------- helpers */

const isProfile = (d: DeviceProfile | DeviceOverrides | undefined): d is DeviceProfile =>
  !!d && typeof (d as DeviceProfile).textureBudgetBytes === 'number'

/** Playback speed range (0.5–2, `clock.ts#clampRate`). Kept under its old name for importers. */
export const clampVoiceSpeed = clampRate

/** The half of `PlayerPrefs` the runner owns — the others cross ports. */
function runnerHalf(p: PlayerPrefs | undefined): Partial<RunnerPrefs> {
  if (!p) return {}
  const { subtitles: _s, rate: _rate, voiceSpeed: _v, reducedMotion: _r, ...rest } = p
  return rest
}

/** `rate`, else its older name `voiceSpeed`, else nothing. */
function rateOf(p: PlayerPrefs | undefined): number | undefined {
  if (p?.rate !== undefined) return p.rate
  return p?.voiceSpeed
}

/** The spoken lines of scene `i` — what `AudioPort.preloadLines` needs decoding. */
function lineCuesOfScene(bundle: Bundle, i: number): LineCue[] {
  const scene = bundle.scenes[i]
  if (!scene) return []
  return cuesOf(scene).filter(isLineCue)
}

/**
 * Index of the word to highlight at `t` seconds into the line, or `-1`
 * before the first word starts.
 *
 * Monotone by construction: the last word whose `s <= t`. In a gap between
 * words the previous word stays lit rather than the band going dark, which
 * reads better at 2.1 words/s and — more importantly — makes the function
 * total, so a seek can never disagree with a play-through. `subtitles.ts`
 * re-exports it; this is the only implementation.
 */
export function wordIndexAt(words: readonly LineWord[], t: number): number {
  const n = words.length
  if (n === 0) return -1
  const time = Number.isFinite(t) ? t : 0
  const first = words[0]!
  if (time < first.s) return -1
  let lo = 0
  let hi = n - 1
  let best = 0
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const word = words[mid]!
    if (word.s <= time) {
      best = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return best
}

/**
 * Word index inside the live line. Uses the baked `AudioRef.words[]` when the
 * line has real Kokoro timings, and proportional word timing otherwise —
 * the `estimate.py`-style client-side fallback of BUILD-OVERRIDES.
 */
export function wordIndexOfLine(cue: LineCue, tLocal: number): number {
  const audio = (cue as { audio?: { words?: LineWord[] } | null }).audio
  const words = audio?.words
  if (words && words.length > 0) return wordIndexAt(words, tLocal)
  const text = lineText(cue)
  const n = text.trim() === '' ? 0 : text.trim().split(/\s+/).length
  const dur = cueDur(cue)
  if (n === 0 || dur <= 0) return -1
  return Math.min(n - 1, Math.floor((tLocal / dur) * n))
}

/* ------------------------------------------------------------ the player */

export async function createPlayer(
  host: HTMLElement | null,
  rawBundle: Bundle,
  opts: CreatePlayerOptions = {},
): Promise<PlayerHandle> {
  // The wire allows `cast`/`props` as arrays and `src` for asset urls (SKEL-STORY §3.5).
  const bundle = normalizeBundle(rawBundle)
  const device: DeviceProfile = isProfile(opts.device)
    ? opts.device
    : host
      ? currentDevice({ budgetTable: bundle.options.texture_budget_mib, ...(opts.device ?? {}) })
      : headlessDevice(opts.device ?? {})

  const baseUrl = opts.baseUrl ?? (opts.bundleUrl ? baseUrlOfBundle(opts.bundleUrl) : './')
  const audio = opts.audio ?? nullAudioPort()
  const hud = opts.hud ?? nullHudPort()
  const now = opts.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()))

  // Listeners are stored erased: a union of handler types is contravariant and will not unify.
  type AnyListener = (...args: never[]) => void
  const listeners = new Map<keyof PlayerEvents, Set<AnyListener>>()
  const emit = <K extends keyof PlayerEvents>(ev: K, ...args: Parameters<PlayerEvents[K]>): void => {
    const set = listeners.get(ev)
    if (!set) return
    for (const cb of [...set]) (cb as unknown as (...a: Parameters<PlayerEvents[K]>) => void)(...args)
  }

  let loading = { active: true, progress: 0 }
  /** Wall ms a quiet (play-through) scene load began; the loading card waits `LOAD_CARD_DELAY_MS`. */
  let quietLoadSince: number | null = null
  /**
   * The scene whose load the child is waiting on: scene 0 before "Tap to start", then the runner's
   * own scene loads; null between them. Only its progress moves the loading card. A background
   * `preloadNext` (the part after this one, or a part that just arrived with a new pack page) must
   * never raise the card over a story that is playing.
   */
  let foregroundLoad: number | null = 0
  /** Waiting for the runner's scene load in flight to settle; the frame loop lets them go. */
  let loadWaiters: (() => void)[] = []
  const loader = new BundleLoader({
    bundle,
    baseUrl,
    device,
    textures: opts.textures ?? (host && opts.renderer !== false ? pixiTexturePort() : nullTexturePort()),
    onProgress: (p) => {
      const was = loading
      if (p.sceneIndex === foregroundLoad) loading = { active: p.ratio < 1 && quietLoadSince === null, progress: p.ratio }
      emit('progress', p)
      // One HUD push per visible step of the bar (it shows whole percent), not
      // one per texture: a long story loads 40-70 of them in a burst.
      if (was.active !== loading.active || Math.round(was.progress * 100) !== Math.round(loading.progress * 100)) pushState()
    },
  })

  const clock = new StoryClock()
  let subtitles = opts.prefs?.subtitles ?? bundle.options.subtitles_default
  // The preferences that cross a port. They start from the device and the
  // caller; the grown-up panel moves them through `setPrefs` (§8.6).
  let reducedMotion = opts.prefs?.reducedMotion ?? device.reducedMotion
  let lowDistraction = opts.prefs?.lowDistraction ?? false
  let rate = 1
  let wordIndex = -1
  let needsGesture = true
  let destroyed = false
  /**
   * Playback speed: the story clock and the voice get the same number, so a
   * line that masters the clock and the free clock between lines move at one
   * speed. Everything drawn is a function of story time and follows by itself
   * (walks, clips, camera, particles, bubbles, karaoke).
   */
  const applyRate = (r: number): void => {
    rate = clampRate(r)
    clock.setRate(rate)
    if (audio.setRate) audio.setRate(rate)
    else audio.setVoiceSpeed?.(rate)
  }
  applyRate(rateOf(opts.prefs) ?? 1)

  /**
   * Decode scene `i`'s baked voice, but never make the child wait on it.
   *
   * Same-origin mp3s of a few tens of KB each, so in practice this resolves
   * long before the loading bar does; the timeout exists so one stalled
   * request cannot hold the tap gate shut. A line that loses the race is not
   * lost — it plays through Web Speech and its buffer keeps warming.
   */
  const preloadVoice = async (i: number, budgetMs = VOICE_PRELOAD_MS): Promise<void> => {
    const cues = lineCuesOfScene(bundle, i)
    const sounds = bundle.scenes[i] ? cuesOf(bundle.scenes[i]!).filter((c) => c.op === 'sfx').map((c) => c.sound) : []
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      await Promise.race([
        Promise.all([audio.preloadLines?.(cues), audio.preloadSounds?.(sounds)]),
        new Promise<void>((resolve) => {
          timer = setTimeout(resolve, budgetMs)
        }),
      ])
    } catch {
      /* a voice that will not decode is a Web Speech line, not a broken story */
    } finally {
      if (timer !== undefined) clearTimeout(timer)
    }
  }

  /** How much of the story exists yet (`stream.ts`); a finished story is `complete`. */
  let stream: StreamInfo = streamOf(bundle)
  /** "End here" (or the story is gone): this session waits for no more parts, whatever arrives. */
  let streamEnded = false
  /** ...and the page said why: the story's job stopped (`endStream({failed: true})`). */
  let endedFailed = false
  /**
   * The newest revision's cast and prop tables (`mergeGrownBundle`), swapped in at the next scene
   * load: `world.ts` rebuilds its views from them in `setScene`, never under a built scene.
   */
  let pendingEntities: (() => void) | null = null
  /** The most parts an open revision promised; 0 for a story first seen already failed. */
  let promisedParts = isOpen(stream) ? stream.partsTotal : 0
  /** Wall ms the story last showed signs of life to a waiting child: its last revision, or the wait's start. */
  let stallFrom = now()
  /** The child has waited `STREAM_STALL_MS` with nothing new (the frame loop keeps it). */
  let streamStalled = false

  const runner = new TimelineRunner({
    bundle,
    audio,
    clock,
    now,
    prefs: {
      mode: opts.mode ?? 'tap',
      ...runnerHalf(opts.prefs),
    },
    loadScene: async (index, { entry }) => {
      // A jump raises the loading card at once. A play-through or "Again" keeps the frozen last
      // frame up instead (the renderer snapshots it, and the transition carries it away), and the
      // card comes up only if the load is still going after LOAD_CARD_DELAY_MS (the frame loop).
      if (entry === 'jump') {
        loading = { active: true, progress: 0 }
        pushState()
      } else {
        quietLoadSince = now()
      }
      foregroundLoad = index
      // before the preload: the loader lists the new tables' puppet parts, the renderer builds them
      pendingEntities?.()
      pendingEntities = null
      try {
        // textures and baked voice side by side, not one after the other
        await Promise.all([loader.preloadScene(index), preloadVoice(index)])
        // Captured for every entry: a jump, too, shows the frozen frame while it builds, then cuts.
        await renderer?.setScene(index, { capture: true })
      } finally {
        quietLoadSince = null
        // a later load that overlapped this one keeps its own
        if (foregroundLoad === index) foregroundLoad = null
      }
      loading = { active: false, progress: 1 }
      pushState()
    },
  })

  audio.onWord((i) => {
    wordIndex = i
    pushState()
  })
  // A story still being made waits at the end of its last ready part instead of ending there.
  runner.setStreamOpen(expectsMore(stream))

  /* ------------------------------------------------------------- state */

  /** Recomputed when a story still being made grows (`appendBundle`). */
  let timeline = storyTimeline(bundle)
  let uiState: PlayerUiState = buildState()

  /**
   * Scene seconds as the grown-up sees them: a transition's pre-roll (the clock below 0) reads 0
   * and the story's ending (past the last scene's end) reads its end, so the seek bar never steps
   * back across a play-through boundary and never runs on after the last line.
   */
  function sceneTNow(): number {
    return Math.min(runner.scene.duration, Math.max(0, runner.t))
  }

  /** Story seconds across every scene; the whole story once it has ended. */
  function storyTNow(): number {
    if (runner.phase === 'ended') return timeline.total
    const start = timeline.starts[runner.sceneIndex] ?? 0
    return Math.min(timeline.total, start + sceneTNow())
  }

  function buildState(): PlayerUiState {
    const line = runner.line
    const prefs = runner.getPrefs()
    const tLocal = line ? Math.max(0, runner.t - line.t) : 0
    return {
      mode: prefs.mode,
      phase: runner.phase,
      holdKind: runner.holdKind,
      holdLabel: runner.holdLabel,
      needsGesture,
      loading: { ...loading },
      canReplayLine: runner.canReplayLine,
      sceneIndex: runner.sceneIndex,
      sceneCount: runner.sceneCount,
      t: sceneTNow(),
      sceneDuration: runner.scene.duration,
      storyT: storyTNow(),
      storyDuration: timeline.total,
      sceneStarts: timeline.starts,
      scrubbing: runner.isScrubbing,
      subtitle: line
        ? {
            text: lineText(line),
            wordIndex: wordIndex >= 0 ? wordIndex : wordIndexOfLine(line, tLocal),
            speaker: lineSpeaker(line),
          }
        : null,
      dimmed: runner.dimmed,
      questions: prefs.questions,
      subtitles,
      reducedMotion,
      lowDistraction,
      deviceClass: device.class,
      rate,
      stream,
      streamStalled: streamStalled && runner.isWaiting,
      stoppedShort: stream.state === 'failed' && !playedToTheEnd(),
    }
  }

  /**
   * A failed story that got every part out anyway (it failed in the final bake, after the last
   * publish): as many parts as any revision promised, or the last one carries "The End" (only the
   * last part ever gets it).
   */
  function playedToTheEnd(): boolean {
    const n = bundle.scenes.length
    return (promisedParts > 0 && n >= promisedParts) || (n > 0 && endingFor(bundle, n - 1) !== null)
  }

  /**
   * The waiting child's stall clock: it starts when the wait does (and at every revision), and after
   * `STREAM_STALL_MS` with nothing new the wait offers "End here" — a story whose job stopped
   * without rewriting its `stream` block would otherwise hold the child there for good.
   */
  function checkStall(): void {
    if (!runner.isWaiting) {
      stallFrom = Math.max(stallFrom, lastWall)
      if (streamStalled) streamStalled = false
      return
    }
    const stalled = !streamEnded && expectsMore(stream) && lastWall - stallFrom >= STREAM_STALL_MS
    if (stalled === streamStalled) return
    streamStalled = stalled
    pushState()
  }

  function pushState(): void {
    if (destroyed) return
    uiState = buildState()
    hud.setState(uiState)
    emit('state', uiState)
  }

  runner.on('state', pushState)
  runner.on('line', (cue) => {
    wordIndex = -1
    audio.duck(true)
    emit('line', cue)
  })
  runner.on('lineEnd', () => {
    audio.duck(false)
  })
  runner.on('hold', (kind, cue) => emit('hold', kind, cue.label))
  runner.on('nudge', (i, kind) => emit('nudge', i, kind))
  runner.on('sceneChange', (i) => emit('sceneChange', i))
  runner.on('ended', () => emit('ended'))

  /* ------------------------------------------------------ scene 0 bytes */

  // Scene 0's textures and its baked voice start downloading now: side by side, and beside the
  // renderer's own start-up below (chunk imports, the WebGL context). One after another, each
  // was a wait of its own before "Tap to start" on a phone network. Neither rejects: a missing
  // asset or voice is warned about and the story plays without it.
  const scene0 = Promise.all([loader.preloadScene(0), preloadVoice(0)])

  /* ---------------------------------------------------------- renderer */

  let renderer: RendererPort | null = null
  const rendererOpt = opts.renderer
  if (rendererOpt) {
    renderer = rendererOpt
  } else if (host && rendererOpt !== false) {
    try {
      const [{ createStage }, { createWorldRenderer }, { mountBubbleLayer, bubbleMaxChars }] = await Promise.all([
        import('./stage'),
        import('./world'),
        import('./bubbles'),
      ])
      const stage = await createStage(host, { device })
      const maxChars = bubbleMaxChars((bundle.options as { device?: string }).device ?? device.class)
      const world = createWorldRenderer(stage, { bundle, loader, device })
      world.setReducedMotion(reducedMotion)
      world.setLowDistraction?.(lowDistraction)
      // §8.6: the youngest band plays with subtitles OFF, so the bubble is the
      // only place a `say` line is *seen*. Mount it, or that child gets silence
      // on screen. A failure here must still leave a playable story.
      try {
        world.setBubbleLayer(await mountBubbleLayer(world.layers.bubbles as never, { maxChars }))
      } catch (err) {
        console.warn('player: speech bubbles unavailable', err)
      }
      renderer = world
    } catch (err) {
      // A browser with no WebGL and no canvas still plays the story: the HUD
      // shows the subtitle band and the voice keeps going. Honest degradation.
      console.warn('player: renderer unavailable, running audio-only', err)
      emit('error', err)
      renderer = null
    }
  }

  /* ------------------------------------------------------------ anchors */

  /**
   * The `?` and the tap-gate hand are DOM (they keep the 88 px floor) but they
   * belong beside the speaker, so the renderer's bubble layer supplies the
   * point and the HUD does the pixels (§8.6). Pushed only when it actually
   * moved: `setAnchors` re-lays the HUD out, and that must not happen 60×/s.
   */
  let lastAnchorKey = ''
  const anchorKey = (p: readonly [number, number] | null | undefined): string =>
    p ? `${Math.round(p[0] * 2)},${Math.round(p[1] * 2)}` : '-'

  function pushAnchors(): void {
    if (!hud.setAnchors || !renderer?.anchors) return
    const a = renderer.anchors()
    const key = `${anchorKey(a.question)}|${anchorKey(a.gate)}`
    if (key === lastAnchorKey) return
    lastAnchorKey = key
    hud.setAnchors({ question: a.question ?? null, gate: a.gate ?? null })
  }

  /* ------------------------------------------------------------- frames */

  const frames: FrameSource = opts.frames ?? rafFrameSource()
  let lastWall = now()
  frames.start((dtMs) => {
    if (destroyed) return
    lastWall = now()
    runner.tick(dtMs)
    // A seek or a scene key that came in during a "Next"'s load goes ahead once it has landed.
    if (loadWaiters.length > 0 && !runner.loadingScene) releaseLoadWaiters()
    // A quiet scene load that is taking a while: now the child gets the loading card.
    if (quietLoadSince !== null && !loading.active && lastWall - quietLoadSince >= LOAD_CARD_DELAY_MS) {
      quietLoadSince = null
      loading = { active: true, progress: loading.progress }
      pushState()
    }
    checkStall()
    if (renderer) {
      const gated = runner.gatedCue as { actor?: unknown } | null
      renderer.render(runner.snapshot(), {
        bundle,
        sceneIndex: runner.sceneIndex,
        device,
        loader,
        reducedMotion,
        lowDistraction,
        wallMs: lastWall,
        holdKind: runner.holdKind,
        gatedActor: typeof gated?.actor === 'string' ? gated.actor : null,
        sceneT: runner.sceneT,
        entry: runner.entry,
        // after a "Next" (and under a pause pressed during the load): the frame on screen stays put
        between: (runner.phase === 'holding' && runner.holdKind === null) || runner.loadingScene,
        outro: runner.inOutro || (runner.phase === 'ended' && endingFor(bundle, runner.sceneIndex) !== null),
      })
      pushAnchors()
    }
    // Karaoke without a word callback (estimated timing) still needs a repaint,
    // and so does the seek bar's elapsed time between lines (a walk, a pause
    // in the script) — otherwise "0:12" would sit still while the story moves.
    if (runner.line || runner.phase === 'playing') pushState()
  })

  const onResize = (): void => renderer?.resize()
  if (typeof window !== 'undefined' && host) window.addEventListener('resize', onResize)

  /* ------------------------------------------------------------ preload */

  // Scene 0 (textures + baked voice, started above) before the tap gate lifts. `playLine` never
  // blocks, so a line whose mp3 is not decoded yet is *spoken by Web Speech* and the buffered
  // take is only heard on a replay — the child would meet the robot first, every sentence, and
  // the clock would never be audio-mastered. The voice wait is bounded, because a story with no
  // voice must still start promptly (§8.4).
  await scene0
  await renderer?.setScene(0)
  if (foregroundLoad === 0) foregroundLoad = null
  loading = { active: false, progress: 1 }
  pushState()

  /* --------------------------------------------------------- story seek */

  /**
   * One story-level seek. Inside the current scene it is `runner.seek`, which
   * is synchronous and deterministic; anywhere else it is a scene jump, which
   * has to load the scene first and so returns its promise.
   */
  function applyStorySeek(storyT: number): Promise<void> | null {
    const { sceneIndex, t } = locateStoryTime(timeline, storyT)
    const phase = runner.phase
    if (sceneIndex === runner.sceneIndex && phase !== 'ended') {
      runner.seek(t)
      return null
    }
    // Paused stays paused; playing, a hold and the end all play on from there.
    return runner.gotoScene(sceneIndex, { at: t, autoplay: phase !== 'paused' })
  }

  let seekBusy: Promise<void> | null = null
  let seekPending: number | null = null

  /**
   * Settles once the runner has no scene load in flight — at once when it has none. A "Next" keeps
   * the frozen last frame up and raises no loading card for a while (`LOAD_CARD_DELAY_MS`), so the
   * seek bar and the keys stay live through its load: a seek then would land in the part being torn
   * down (voicing its line under the frozen frame) and a jump would race that load with a second.
   */
  function loadSettled(): Promise<void> {
    if (destroyed || !runner.loadingScene) return Promise.resolve()
    return new Promise<void>((resolve) => loadWaiters.push(resolve))
  }

  function releaseLoadWaiters(): void {
    const waiting = loadWaiters
    loadWaiters = []
    for (const resolve of waiting) resolve()
  }

  /** A scene key (skip, previous, replay, restart) pressed during a "Next"'s load: the newest runs after it. */
  let sceneKeyPending: (() => Promise<void>) | null = null
  function afterLoad(go: () => Promise<void>): void {
    if (!runner.loadingScene) {
      void go()
      return
    }
    const queued = sceneKeyPending !== null
    sceneKeyPending = go
    if (queued) return
    void loadSettled().then(() => {
      const next = sceneKeyPending
      sceneKeyPending = null
      if (next && !destroyed) afterLoad(next)
    })
  }

  function seekStory(storyT: number): Promise<void> {
    if (destroyed) return Promise.resolve()
    const target = Math.min(timeline.total, Math.max(0, Number.isFinite(storyT) ? storyT : 0))
    // A scene is loading: remember only the newest target. A drag that fires
    // forty `move`s during one load then costs one more jump, not forty.
    if (seekBusy) {
      seekPending = target
      return seekBusy
    }
    // The runner's own load (a "Next") is in flight: the seek waits for it, like one of ours.
    const waitForLoad = runner.loadingScene
    const first = waitForLoad ? null : applyStorySeek(target)
    if (!first && !waitForLoad) return Promise.resolve()
    if (waitForLoad) seekPending = target
    seekBusy = (async () => {
      let step: Promise<void> | null = first ?? loadSettled()
      while (step) {
        try {
          await step
        } catch (err) {
          emit('error', err)
        }
        if (destroyed || seekPending === null) break
        const next: number = seekPending
        seekPending = null
        step = applyStorySeek(next)
      }
    })().finally(() => {
      seekBusy = null
    })
    return seekBusy
  }

  function setScrubbing(on: boolean): void {
    if (destroyed) return
    if (on || !seekBusy) {
      runner.setScrubbing(on)
      return
    }
    // Let go while a scene is still loading: un-freeze once it has landed, or
    // the voice would restart in the scene the drag is leaving.
    void seekBusy.then(() => runner.setScrubbing(false))
  }

  /* ------------------------------------------------- a story being made */

  /**
   * A newer revision of a story still being made, folded into `bundle` in place (`stream.ts`).
   * Stream first, then the new parts: a final bundle that closes the stream and brings the last
   * parts at once still plays them (the runner's `setStreamOpen(false)` goes on to what arrived).
   */
  function appendBundle(raw: Bundle): MergeResult {
    if (destroyed) return { addedScenes: 0, addedAssets: [], stream }
    const next = normalizeBundle(raw)
    const { result, applyEntities } = mergeGrownBundle(bundle, next, { currentScene: runner.sceneIndex })
    if (result.addedAssets.length > 0) loader.registerAssets(result.addedAssets)
    // The new parts' cast and props; with no new part the scenes here keep the tables they came with.
    if (result.addedScenes > 0) pendingEntities = applyEntities
    const was = stream
    stream = result.stream
    if (isOpen(stream)) promisedParts = Math.max(promisedParts, stream.partsTotal)
    // the page said the story stopped (`endStream({failed})`): a late part still lands, but a revision
    // written before the stop does not make it "streaming" again
    if (endedFailed && isOpen(stream)) stream = { ...stream, state: 'failed', partsTotal: stream.partsReady, resumeAt: null }
    const changed = !sameStream(was, stream)
    // a sign of life: a waiting child's stall clock starts over
    if (changed || result.addedScenes > 0) {
      stallFrom = now()
      streamStalled = false
    }
    runner.setStreamOpen(!streamEnded && expectsMore(stream))
    timeline = storyTimeline(bundle)
    if (changed) emit('stream', stream)
    pushState()
    if (result.addedScenes > 0) {
      runner.scenesAppended()
      // the part after the one playing may be one of them: into the cache while this one plays
      void loader.preloadNext(runner.sceneIndex, Math.max(0, runner.t))
      void preloadVoice(runner.sceneIndex + 1)
    }
    return result
  }

  /** `PlayerHandle.endStream`: nothing more is waited for; `failed` marks the story as stopped short. */
  function endStream(opts: { failed?: boolean } = {}): void {
    if (destroyed) return
    const fail = opts.failed === true && isOpen(stream)
    if (streamEnded && !fail) return
    streamEnded = true
    if (fail) {
      endedFailed = true
      stream = { ...stream, state: 'failed', partsTotal: stream.partsReady, resumeAt: null }
    }
    runner.setStreamOpen(false)
    emit('stream', stream)
    pushState()
  }

  /* ------------------------------------------------------------ handle */

  const handle: PlayerHandle = {
    bundle,
    device,
    runner,
    loader,
    get timeline() {
      return timeline
    },
    get stream() {
      return stream
    },
    get streamEnded() {
      return streamEnded
    },
    get state() {
      return uiState
    },
    appendBundle,
    endStream,

    async start() {
      try {
        await audio.unlock()
      } catch (err) {
        emit('error', err)
      }
      needsGesture = false
      runner.start()
      pushState()
      // the clock may be in scene 0's opening (below 0): the loader thinks in scene seconds
      void loader.preloadNext(runner.sceneIndex, Math.max(0, runner.t))
      void preloadVoice(runner.sceneIndex + 1)
    },
    play() {
      needsGesture = false
      runner.play()
    },
    pause() {
      runner.pause()
    },
    toggle() {
      if (runner.phase === 'paused') runner.play()
      else runner.pause()
    },
    seek(t) {
      runner.seek(t)
    },
    seekStory,
    setScrubbing,
    replayLastLine() {
      return runner.replayLastLine()
    },
    skipScene() {
      return runner.skipScene()
    },
    previousScene() {
      return runner.previousScene()
    },
    replayScene() {
      return runner.replayScene()
    },
    restart() {
      return runner.gotoScene(0, { autoplay: true })
    },
    tap(source: TapSource = 'stage') {
      runner.onTap(source)
    },
    control(c) {
      switch (c) {
        case 'play':
          handle.play()
          return
        case 'pause':
          handle.pause()
          return
        case 'next':
          runner.onTap('hud')
          return
        case 'again':
          runner.replayLastLine()
          return
        // a scene key pressed while a "Next" loads goes after that load, not beside it
        case 'replay_scene':
          afterLoad(() => runner.replayScene())
          return
        case 'previous_scene':
          afterLoad(() => runner.previousScene())
          return
        case 'skip_scene':
          afterLoad(() => runner.skipScene())
          return
        case 'restart':
          afterLoad(() => handle.restart())
          return
        case 'exit':
          handle.pause()
          return
        case 'end_stream':
          // "End here" on a story that goes on later (or it was deleted): nothing more is waited for
          endStream()
          return
        default:
          return
      }
    },
    setMode(m) {
      runner.setPrefs({ mode: m })
    },
    setPrefs(p) {
      if (p.subtitles !== undefined) subtitles = p.subtitles
      // Speed is the clock's and the audio port's, reduced motion is the renderer's.
      const nextRate = rateOf(p)
      if (nextRate !== undefined) applyRate(nextRate)
      if (p.reducedMotion !== undefined && p.reducedMotion !== reducedMotion) {
        reducedMotion = p.reducedMotion
        renderer?.setReducedMotion?.(reducedMotion)
      }
      const rest = runnerHalf(p)
      if (p.lowDistraction !== undefined && p.lowDistraction !== lowDistraction) {
        lowDistraction = p.lowDistraction
        renderer?.setLowDistraction?.(lowDistraction)
      }
      if (Object.keys(rest).length > 0) runner.setPrefs(rest)
      pushState()
    },
    on(ev, cb) {
      let set = listeners.get(ev)
      if (!set) {
        set = new Set<AnyListener>()
        listeners.set(ev, set)
      }
      const fn = cb as AnyListener
      set.add(fn)
      return () => {
        set?.delete(fn)
      }
    },
    destroy() {
      if (destroyed) return
      destroyed = true
      frames.stop()
      releaseLoadWaiters()
      if (typeof window !== 'undefined' && host) window.removeEventListener('resize', onResize)
      runner.destroy()
      renderer?.destroy()
      void loader.destroy()
    },
  }

  hud.onTap((source) => handle.tap(source))
  hud.onControl((c) => handle.control(c))
  // The seek bar never goes through `tap()`: a seek is not a stage tap, so the
  // runner's tap policy (swallow during a line, continue at a hold) never sees it.
  hud.onSeek?.((req) => {
    if (req.scrub === 'start') handle.setScrubbing(true)
    void handle.seekStory(req.t)
    if (req.scrub === 'end') handle.setScrubbing(false)
  })

  // Scene N+1 is background-loaded during the scene, uploaded during the hold (§8.5).
  runner.on('sceneChange', (i) => {
    void loader.preloadNext(i, 0)
    void preloadVoice(i + 1)
    if (i >= 2) void loader.unloadScene(i - 2, [i - 1, i])
  })

  if (opts.autoplay) await handle.start()
  else pushState()

  return handle
}
